import React, { useEffect, useState, useRef } from "react";
import {Box, Button, ButtonGroup, Flex, HStack, IconButton, Input, SkeletonText, Text,} from '@chakra-ui/react';
import { FaLocationArrow, FaTimes } from 'react-icons/fa';
import {useJsApiLoader, GoogleMap, Marker, Autocomplete, DirectionsRenderer, } from '@react-google-maps/api';
import { saveGoogleMap } from "../../../redux/features/userAddressInfoSlice.js";
import MetaData from "../../layout/MetaData";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ChakraProvider, theme } from '@chakra-ui/react';
import "../../../css/Google.css";
import toast from 'react-hot-toast';

const center = { lat: 24.8607, lng: 67.0011 };

const GoogleMapsAddress = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
  });

  const googleMapState = useSelector((state) => state.userAddressInfo.googleMap); // Accessing Redux state

  const [map, setMap] = useState(/** @type google.maps.Map */ (null));
  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [distance, setDistance] = useState();
  const [originCoords, setOriginCoords] = useState({ lat: null, lng: null });
  const [originAutocomplete, setOriginAutocomplete] = useState(null);

  const originRef = useRef();
  const destiantionRef = useRef();

  if (!isLoaded) {
    return <SkeletonText />;
  }

  async function calculateRoute() {
    if (!originRef.current.value || !destiantionRef.current.value) {
      toast.error("Please enter both origin and destination.");
      return;
    }
  
    if (!window.google) {
      console.error("Google Maps API is not loaded.");
      return;
    }
  
    const directionsService = new window.google.maps.DirectionsService();
  
    // Extract both coordinates and the string address
    const origin = {
      lat: originCoords.lat || null,
      lng: originCoords.lng || null,
      address: originRef.current.value, // Always store the input value
    };
  
    const destination = destiantionRef.current.value;
  
    try {
      const results = await directionsService.route({
        origin: origin.lat && origin.lng ? { lat: origin.lat, lng: origin.lng } : origin.address,
        destination,
        travelMode: window.google.maps.TravelMode.DRIVING,
      });
  
      setDirectionsResponse(results);
      setDistance(results.routes[0].legs[0].distance.text);
  
      // Save both address and coordinates in Redux
      dispatch(saveGoogleMap({
        origin: origin.address,
        originLatitude: origin.lat,
        originLongitude: origin.lng,
        destination,
        distance: results.routes[0].legs[0].distance.text,
      }));
  
    } catch (error) {
      console.error("Error calculating route:", error);
      toast.error("Failed to calculate route. Please try again.");
    }
  }
   
  
  const handleOriginPlaceSelect = () => {
    if (!originAutocomplete) {
      console.error("Autocomplete instance not set.");
      return;
    }
  
    const place = originAutocomplete.getPlace();
    
    if (!place || !place.geometry) {
      console.error("Invalid place selected.");
      toast.error("Please select a valid location from suggestions.");
      return;
    }
  
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const formattedAddress = place.formatted_address || place.name;
  
    console.log("Selected Place:", formattedAddress, lat, lng);
  
    // Update state
    setOriginCoords({ lat, lng });
  
    // Dispatch to Redux
    dispatch(saveGoogleMap({
      origin: formattedAddress,
      originLatitude: lat,
      originLongitude: lng,
    }));
  };
    
  const submiHandler = async (e) => {
    e.preventDefault();
  
    if (!googleMapState || !googleMapState.origin || !googleMapState.destination || !googleMapState.distance) {
      toast.error("Please calculate the route first.");
      return;
    }
  
    console.log("Submitting location:", googleMapState);
  
    navigate("/create-booking/local");
  };
  

  function clearRoute() {
    setDirectionsResponse(null);
    setDistance('');
    originRef.current.value = '';
    destiantionRef.current.value = '';
  }

  return (
    <>
      <MetaData title={"Set Starting and Destination Area"} />
      <ChakraProvider theme={theme}>
        <Flex
          position='relative'
          flexDirection='column'
          alignItems='center'
          h='100vh'
          w='100vw'
        >
          <Box position='absolute' left={0} top={0} h='100%' w='100%'>
            {/* Google Map Box */}
            <GoogleMap
              center={center}
              zoom={9}
              mapContainerStyle={{ width: '100%', height: '100%' }}
              options={{
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
              }}
              onLoad={map => setMap(map)}
            >                                 
              <Marker position={center} />
              {directionsResponse && (
                <DirectionsRenderer directions={directionsResponse} />
              )}
            </GoogleMap>
          </Box>
          
          <Box
            p={4}
            borderRadius='lg'
            m={4}
            bgColor='white'
            shadow='base'
            minW='container.md'
            zIndex='1'
            id="input-box"
          >

            <HStack spacing={2} justifyContent='space-between' flexDirection={{ base: 'column', md: 'row' }}>
              <Box flexGrow={1} id="input-boxsmall">
              <Autocomplete      
  onLoad={autocomplete => setOriginAutocomplete(autocomplete)} 
  onPlaceChanged={handleOriginPlaceSelect}
  options={{
    componentRestrictions: { country: 'pk' },
    fields: ['formatted_address', 'geometry', 'name'],
  }}>
  <Input type='text' placeholder='Starting Area' ref={originRef} />
</Autocomplete>


              </Box>
              <Box flexGrow={1} mt={{ base: 4, md: 0 }} id="destination-google-maps-box"> 
                <Autocomplete       
                options={{
                componentRestrictions: { country: 'pk' }, // Restrict to Pakistan
                fields: ['formatted_address', 'geometry', 'name'], // Specify fields to return
                }}>
                  <Input
                    type='text'
                    placeholder='Destination Area'
                    ref={destiantionRef}
                  />
                </Autocomplete>
              </Box>

            <br></br>
              <ButtonGroup id="calculaterouteid">
                <Button colorScheme='pink' type='submit' onClick={calculateRoute}>
                  Calculate
                </Button>
              </ButtonGroup>
            </HStack>

            <HStack spacing={4} mt={4} justifyContent='space-between' id="continueanddistanceid">

            <Text id="distanceshowid">Distance: {distance} </Text>

              <Button 
                isDisabled={!googleMapState || !googleMapState.origin || !googleMapState.destination || !googleMapState.distance} 
                colorScheme='pink' 
                type='submit' 
                onClick={submiHandler}
                id="continuebuttonid"
              >
                Continue
              </Button>

 
            </HStack>
          </Box>
        </Flex>
      </ChakraProvider>
    </>
  );
};

export default GoogleMapsAddress;
